import React, { useContext, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import IconButton from '@mui/material/IconButton';
import KeepitalAccountAppSvg from '../../components/_svg/KeepitalAccountAppSvg';
import KeepitalMessengerAppSvg from '../../components/_svg/KeepitalMessengerAppSvg';
import KeepitalAnnounceAppSvg from '../../components/_svg/KeepitalAnnounceAppSvg';
import KeepitalProcurementAppSvg from '../../components/_svg/KeepitalProcurementAppSvg';
import KeepitalBusinessAppSvg from '../../components/_svg/KeepitalBusinessAppSvg';
import KeepitalProductAppSvg from '../../components/_svg/KeepitalProductAppSvg';
import KeepitalMinisiteAppSvg from '../../components/_svg/KeepitalMinisiteAppSvg';
import KeepitalCodeAppSvg from '../../components/_svg/KeepitalCodeAppSvg';
import KeepitalPlanAppSvg from '../../components/_svg/KeepitalPlanAppSvg';
import KeepitalKeepitalAppSvg from '../../components/_svg/KeepitalKeepitalAppSvg';
import KeepitalSuperAppSvg from '../../components/_svg/KeepitalSuperAppSvg';
import KeepitalLogAppSvg from '../../components/_svg/KeepitalLogAppSvg';
import useRoutes from '../../components/_hook/useRoutes';
import useKeepitalRoutes from '../../components/_hook/useKeepitalRoutes';
import Context from '../../context';
import useSuperRoutes from '../../components/_hook/useSuperRoutes';
import KeepitalSubdirectoryAppSvg from '../../components/_svg/KeepitalSubdirectoryAppSvg';
import Joyride from '../../components/Joyride';

interface IProps {
  app: string;
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
    '& a': {
      textDecoration: 'none',
      color: 'inherit',
      display: 'flex',
    },
  },
  drawerHeaderText: {
    fontWeight: 'bold',
    marginTop: '4px',
    display: 'inline',
  },
  guideIconButton: {
    color: theme.palette.mode === 'light' ? '#000' : '#fff',
  },
}));

export default function DrawerHeader(props: IProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { app } = props;
  const { user, isSuperAdmin, company } = useContext(Context);
  const { path } = useRoutes();
  const { path: keepitalPath } = useKeepitalRoutes();
  const { path: superPath } = useSuperRoutes();
  const [
    isRunBusinessAppJoyride,
    setIsRunBusinessAppJoyride,
  ] = useState<boolean>(false);
  const [isRunProductAppJoyride, setIsRunProductAppJoyride] = useState<boolean>(
    false
  );
  const [isRunPlanAppJoyride, setIsRunPlanAppJoyride] = useState<boolean>(
    false
  );
  const [
    isRunProcurementAppJoyride,
    setIsRunProcurementAppJoyride,
  ] = useState<boolean>(false);
  const [
    isRunAnnounceAppJoyride,
    setIsRunAnnounceAppJoyride,
  ] = useState<boolean>(false);

  const userCompanies = user.companies || [];
  return (
    <div className={classes.drawerHeader}>
      <Grid container justifyContent="center" alignItems="center">
        {app === 'account' && (
          <a
            href={keepitalPath('user.dashboard', { userID: user._id })}
            rel="noopener noreferrer"
            target="_blank"
          >
            <KeepitalAccountAppSvg width={32} height={32} />
            &nbsp;&nbsp;
            <Typography
              variant="subtitle1"
              className={classes.drawerHeaderText}
            >
              {t('apps_account')}
            </Typography>
          </a>
        )}
        {app === 'messenger' && (
          <Link
            to={path('messenger.chatrooms', {
              companyID: company ? company._id : userCompanies[0].id,
            })}
          >
            <KeepitalMessengerAppSvg width={32} height={32} />
            &nbsp;&nbsp;
            <Typography
              variant="subtitle1"
              className={classes.drawerHeaderText}
            >
              {t('apps_messenger')}
            </Typography>
          </Link>
        )}
        {app === 'announce' && (
          <>
            <Link
              to={path('announce.dashboard', {
                companyID: company ? company._id : userCompanies[0].id,
              })}
            >
              <KeepitalAnnounceAppSvg width={32} height={32} />
              &nbsp;&nbsp;
              <Typography
                variant="subtitle1"
                className={classes.drawerHeaderText}
              >
                {t('apps_announce')}
              </Typography>
            </Link>
            &nbsp;
            <IconButton
              onClick={async () => {
                setIsRunAnnounceAppJoyride(true);
              }}
              size="small"
              className={classes.guideIconButton}
            >
              <HelpOutlineIcon />
            </IconButton>
            <Joyride
              steps={[
                {
                  target: '.announce-drawer-category-list-item-joyride',
                  content: t(
                    'you_can_manage_categories_of_announces_for_your_business'
                  ),
                  disableBeacon: true,
                },
                {
                  target: '.announce-drawer-announces-list-item-joyride',
                  content: t('you_can_manage_announces_of_your_business'),
                  disableBeacon: true,
                },
              ]}
              isRun={isRunAnnounceAppJoyride}
              onRunEnd={() => {
                setIsRunAnnounceAppJoyride(false);
              }}
              isContinuous
              showProgress
              showSkipButton
              scrollOffset={100}
            />
          </>
        )}
        {app === 'procurement' && (
          <>
            <Link
              to={path('procurement.dashboard', {
                companyID: company ? company._id : userCompanies[0].id,
              })}
            >
              <KeepitalProcurementAppSvg width={32} height={32} />
              &nbsp;&nbsp;
              <Typography
                variant="subtitle1"
                className={classes.drawerHeaderText}
              >
                {t('apps_procurement')}
              </Typography>
            </Link>
            &nbsp;
            <IconButton
              onClick={async () => {
                setIsRunProcurementAppJoyride(true);
              }}
              size="small"
              className={classes.guideIconButton}
            >
              <HelpOutlineIcon />
            </IconButton>
            <Joyride
              steps={[
                {
                  target: '.procurement-drawer-incoming-rfq-list-item-joyride',
                  content: t(
                    'you_can_view_requests_for_quotes_that_you_have_received'
                  ),
                  disableBeacon: true,
                },
                {
                  target:
                    '.procurement-drawer-incoming-enquiries-list-item-joyride',
                  content: t(
                    'you_can_view_incoming_enquiries_that_you_have_received'
                  ),
                  disableBeacon: true,
                },
                {
                  target:
                    '.procurement-drawer-incoming-quotations-list-item-joyride',
                  content: t(
                    'you_can_view_incoming_quotations_that_you_have_received'
                  ),
                  disableBeacon: true,
                },
                {
                  target:
                    '.procurement-drawer-incoming-orders-list-item-joyride',
                  content: t(
                    'you_can_view_incoming_orders_that_you_have_received'
                  ),
                  disableBeacon: true,
                },
                {
                  target:
                    '.procurement-drawer-incoming-delivery-notes-list-item-joyride',
                  content: t(
                    'you_can_view_incoming_delivery_notes_that_you_have_received'
                  ),
                  disableBeacon: true,
                },
                {
                  target: '.procurement-drawer-outgoing-rfq-list-item-joyride',
                  content: t(
                    'you_can_view_requests_for_quotes_that_you_have_sent'
                  ),
                  disableBeacon: true,
                },
                {
                  target:
                    '.procurement-drawer-outgoing-enquiries-list-item-joyride',
                  content: t(
                    'you_can_view_outgoing_enquiries_that_you_have_sent'
                  ),
                  disableBeacon: true,
                },
                {
                  target:
                    '.procurement-drawer-outgoing-quotations-list-item-joyride',
                  content: t(
                    'you_can_view_outgoing_quotations_that_you_have_sent'
                  ),
                  disableBeacon: true,
                },
                {
                  target:
                    '.procurement-drawer-outgoing-orders-list-item-joyride',
                  content: t('you_can_view_outgoing_orders_that_you_have_sent'),
                  disableBeacon: true,
                },
                {
                  target:
                    '.procurement-drawer-outgoing-delivery-notes-list-item-joyride',
                  content: t(
                    'you_can_view_outgoing_delivery_notes_that_you_have_sent'
                  ),
                  disableBeacon: true,
                },
              ]}
              isRun={isRunProcurementAppJoyride}
              onRunEnd={() => {
                setIsRunProcurementAppJoyride(false);
              }}
              isContinuous
              showProgress
              showSkipButton
              scrollOffset={100}
            />
          </>
        )}
        {(company || userCompanies.length) && (
          <>
            {app === 'business' && (
              <>
                <Link
                  to={path('business.dashboard', {
                    companyID: company ? company._id : userCompanies[0].id,
                  })}
                >
                  <KeepitalBusinessAppSvg width={32} height={32} />
                  &nbsp;&nbsp;
                  <Typography
                    variant="subtitle1"
                    className={classes.drawerHeaderText}
                  >
                    {t('apps_business')}
                  </Typography>
                </Link>
                &nbsp;
                <IconButton
                  onClick={async () => {
                    setIsRunBusinessAppJoyride(true);
                  }}
                  size="small"
                  className={classes.guideIconButton}
                >
                  <HelpOutlineIcon />
                </IconButton>
                <Joyride
                  steps={[
                    {
                      target: '.business-drawer-profile-list-item-joyride',
                      content: t(
                        'manage_business_profile_where_you_can_update_and_customize_your_business_information'
                      ),
                      disableBeacon: true,
                    },
                    {
                      target: '.business-drawer-branch-list-item-joyride',
                      content: t(
                        'manage_your_branches_or_physical_stores_as_well_as_your_branch_contact_information'
                      ),
                      disableBeacon: true,
                    },
                    {
                      target: '.business-drawer-contact-list-item-joyride',
                      content: t(
                        'you_can_manage_your_business_email_address_and_website_link_here'
                      ),
                      disableBeacon: true,
                    },
                    {
                      target: '.business-drawer-logo-list-item-joyride',
                      content: t(
                        'you_can_upload_or_change_your_business_logo_here'
                      ),
                      disableBeacon: true,
                    },
                    {
                      target: '.business-drawer-banner-list-item-joyride',
                      content: t(
                        'you_can_upload_or_change_your_business_banner_here'
                      ),
                      disableBeacon: true,
                    },
                  ]}
                  isRun={isRunBusinessAppJoyride}
                  onRunEnd={() => {
                    setIsRunBusinessAppJoyride(false);
                  }}
                  isContinuous
                  showProgress
                  showSkipButton
                  scrollOffset={100}
                />
              </>
            )}
            {app === 'product' && (
              <>
                <Link
                  to={path('product.dashboard', {
                    companyID: company ? company._id : userCompanies[0].id,
                  })}
                >
                  <KeepitalProductAppSvg width={32} height={32} />
                  &nbsp;&nbsp;
                  <Typography
                    variant="subtitle1"
                    className={classes.drawerHeaderText}
                  >
                    {t('apps_product')}
                  </Typography>
                </Link>
                &nbsp;
                <IconButton
                  onClick={async () => {
                    setIsRunProductAppJoyride(true);
                  }}
                  size="small"
                  className={classes.guideIconButton}
                >
                  <HelpOutlineIcon />
                </IconButton>
                <Joyride
                  steps={[
                    {
                      target: '.product-drawer-category-list-item-joyride',
                      content: t(
                        'you_can_manage_categories_of_products_or_services_that_your_business_offers'
                      ),
                      disableBeacon: true,
                    },
                    {
                      target: '.product-drawer-product-list-item-joyride',
                      content: t(
                        'you_can_manage_products_or_services_that_your_business_offers'
                      ),
                      disableBeacon: true,
                    },
                    {
                      target:
                        '.product-drawer-import-product-list-item-joyride',
                      content: t(
                        'you_can_import_products_into_your_company_from_a_csv_comma_separated_values_file_you_can_use_it_to_quickly_add_update_multiple_products_at_once_by_uploading_a_file_that_contains_the_product_information_in_a_structured_format'
                      ),
                      disableBeacon: true,
                    },
                    {
                      target: '.product-drawer-import-image-list-item-joyride',
                      content: t(
                        'you_can_import_images_for_your_products_you_can_use_it_to_add_images_to_your_product_listings_or_to_update_the_existing_images_for_your_products'
                      ),
                      disableBeacon: true,
                    },
                  ]}
                  isRun={isRunProductAppJoyride}
                  onRunEnd={() => {
                    setIsRunProductAppJoyride(false);
                  }}
                  isContinuous
                  showProgress
                  showSkipButton
                  scrollOffset={100}
                />
              </>
            )}
            {app === 'minisite' && (
              <Link
                to={path('minisite.dashboard', {
                  companyID: company ? company._id : userCompanies[0].id,
                })}
              >
                <KeepitalMinisiteAppSvg width={32} height={32} />
                &nbsp;&nbsp;
                <Typography
                  variant="subtitle1"
                  className={classes.drawerHeaderText}
                >
                  {t('apps_minisite')}
                </Typography>
              </Link>
            )}
            {app === 'subdirectory' && (
              <Link
                to={path('subdirectory.dashboard', {
                  companyID: company ? company._id : userCompanies[0].id,
                })}
              >
                <KeepitalSubdirectoryAppSvg width={32} height={32} />
                &nbsp;&nbsp;
                <Typography
                  variant="subtitle1"
                  className={classes.drawerHeaderText}
                >
                  {t('apps_subdirectory')}
                </Typography>
              </Link>
            )}
            {app === 'code' && (
              <Link
                to={path('code.dashboard', {
                  companyID: company ? company._id : userCompanies[0].id,
                })}
              >
                <KeepitalCodeAppSvg width={32} height={32} />
                &nbsp;&nbsp;
                <Typography
                  variant="subtitle1"
                  className={classes.drawerHeaderText}
                >
                  {t('apps_code')}
                </Typography>
              </Link>
            )}
            {app === 'plan' && (
              <>
                <Link
                  to={path('plan.dashboard', {
                    companyID: company ? company._id : userCompanies[0].id,
                  })}
                >
                  <KeepitalPlanAppSvg width={32} height={32} />
                  &nbsp;&nbsp;
                  <Typography
                    variant="subtitle1"
                    className={classes.drawerHeaderText}
                  >
                    {t('apps_plans')}
                  </Typography>
                </Link>
                &nbsp;
                <IconButton
                  onClick={async () => {
                    setIsRunPlanAppJoyride(true);
                  }}
                  size="small"
                  className={classes.guideIconButton}
                >
                  <HelpOutlineIcon />
                </IconButton>
                <Joyride
                  steps={[
                    {
                      target: '.plan-drawer-subscription-list-item-joyride',
                      content: t(
                        'you_can_manage_the_subscription_plan_for_your_company'
                      ),
                      disableBeacon: true,
                    },
                    {
                      target: '.plan-drawer-administrator-list-item-joyride',
                      content: t(
                        'you_to_manage_the_admin_users_for_your_company'
                      ),
                      disableBeacon: true,
                    },
                  ]}
                  isRun={isRunPlanAppJoyride}
                  onRunEnd={() => {
                    setIsRunPlanAppJoyride(false);
                  }}
                  isContinuous
                  showProgress
                  showSkipButton
                  scrollOffset={100}
                />
              </>
            )}
          </>
        )}
        {isSuperAdmin && (
          <>
            {app === 'keepital' && (
              <a
                href={superPath('keepital.dashboard')}
                rel="noopener noreferrer"
                target="_blank"
              >
                <KeepitalKeepitalAppSvg width={32} height={32} />
                &nbsp;&nbsp;
                <Typography
                  variant="subtitle1"
                  className={classes.drawerHeaderText}
                >
                  {t('apps_keepital')}
                </Typography>
              </a>
            )}
            {app === 'super' && (
              <a
                href={superPath('super.dashboard')}
                rel="noopener noreferrer"
                target="_blank"
              >
                <KeepitalSuperAppSvg width={32} height={32} />
                &nbsp;&nbsp;
                <Typography
                  variant="subtitle1"
                  className={classes.drawerHeaderText}
                >
                  {t('apps_super')}
                </Typography>
              </a>
            )}
            {app === 'log' && (
              <a
                href={superPath('log.dashboard')}
                rel="noopener noreferrer"
                target="_blank"
              >
                <KeepitalLogAppSvg width={32} height={32} />
                &nbsp;&nbsp;
                <Typography
                  variant="subtitle1"
                  className={classes.drawerHeaderText}
                >
                  {t('log')}
                </Typography>
              </a>
            )}
          </>
        )}
      </Grid>
    </div>
  );
}
